import { useDispatch, useSelector } from "react-redux";
import { MAX_SEGMENTS_COUNT, WEBSITE_ID_KEY } from "../constants";
import {
    getArrayOfSegments,
    getArrayOfSegmentsId,
    getFirstSevenSegments,
    getSegmentsAndWebsitesByGroup,
    getSegmentsIdsFromGroup,
    mergeSegmentIds,
    removeInitialSegmentsIds,
} from "../helpers";
import {
    segmentsGroupIdChanged,
    selectedSegmentsIdChanged,
    dashboardWizardKeyChanged,
    dashboardWizardKeyReset,
} from "app/components/dashboard/widget-wizard/actions/dashboardWizardActionCreators";
import { useCallback, useEffect, useRef, useState } from "react";
import debounce from "lodash/debounce";
import isEmpty from "lodash/isEmpty";
import type { ICustomSegmentAccount, UniqueSegments } from "app/services/segments/segmentsApiService";
import {
    selectAccountSegments,
    selectCurrentDashboard,
    selectIsEditing,
    selectCustomSegmentsGroups,
    selectUniqueSegments,
    selectWidgetFilters,
} from "../selectors";
import type { SelectedSegmentsId } from "../types";
import { swSettings } from "app/common/services/swSettings";
import { updateUniqueSegmentsIds } from "app/actions/segmentsModuleActions";

const useSegments = (customDashboard) => {
    const dispatch = useDispatch();

    const accountSegments = useSelector(selectAccountSegments);
    const segmentsGroups = useSelector(selectCustomSegmentsGroups);
    const widgetFilters = useSelector(selectWidgetFilters);
    const defaultUniqueSegments = useSelector(selectUniqueSegments);

    const currentDashboard = useSelector(selectCurrentDashboard);
    const isEditing = useSelector(selectIsEditing);

    const [segments, setSegments] = useState<ICustomSegmentAccount[] | []>([]);
    const [selectedSegmentsIds, setSelectedSegmentsIds] = useState<SelectedSegmentsId | Record<string, never>>({});
    const [selectedSegments, setSelectedSegments] = useState<ICustomSegmentAccount[]>([]);

    const [selectedGroupId, setSelectedGroupId] = useState<string>(widgetFilters?.segmentsGroupId);
    const [selectedGroupSegments, setSelectedGroupSegments] = useState<ICustomSegmentAccount[]>([]);
    const [segmentsGroupsWithFlags, setSegmentsGroupsWithFlags] = useState([]);

    const [uniqueSegmentsIds, setUniqueSegmentsIds] = useState<UniqueSegments>();

    const initialUniqueSegments = useRef(defaultUniqueSegments);

    useEffect(() => {
        const selectedSegmentsId = widgetFilters?.selectedSegmentsId;
        const hasWebsites =
            selectedSegmentsId && !!selectedSegmentsId?.find((id: string) => id.includes(WEBSITE_ID_KEY));

        const segmentsIds = {};

        if (selectedSegmentsId && segmentsGroups) {
            const segmentsIdsFromGroup = getSegmentsIdsFromGroup(selectedGroupId, segmentsGroups);

            selectedSegmentsId.forEach((id: string) => {
                segmentsIds[id] = true;
            });

            setSelectedSegmentsIds(segmentsIds);
            setSegments(
                getSegmentsAndWebsitesByGroup(
                    accountSegments,
                    segmentsGroups,
                    selectedGroupId,
                    segmentsIdsFromGroup,
                    hasWebsites,
                ),
            );
        }
    }, [accountSegments]);

    useEffect(() => {
        const segmentsGroupId = customDashboard?.widget?.filters?.segmentsGroupId;

        if (!segmentsGroupId && !isEmpty(selectedSegmentsIds)) {
            const selectedSegmentsId = Object.keys(selectedSegmentsIds);

            dispatch(selectedSegmentsIdChanged(customDashboard, selectedSegmentsId));
        }

        if (!segmentsGroupId && selectedGroupId) {
            dispatch(segmentsGroupIdChanged(customDashboard, selectedGroupId));
        }
    }, [widgetFilters]);

    useEffect(() => {
        if (selectedGroupId && segmentsGroups) {
            updateSegmentsByGroupId(selectedGroupId);
        }
    }, [segmentsGroups]);

    useEffect(
        () => () => {
            dispatch(selectedSegmentsIdChanged(customDashboard, []));
            dispatch(dashboardWizardKeyReset(customDashboard));
        },
        [],
    );

    useEffect(() => {
        if (segmentsGroups && selectedGroupId) {
            const segmentsIdsFromGroup = getSegmentsIdsFromGroup(selectedGroupId, segmentsGroups);

            if (isEditing) {
                const filteredSegmentsIds = removeInitialSegmentsIds(
                    initialUniqueSegments.current,
                    segmentsIdsFromGroup.ids,
                );

                setUniqueSegmentsIds(filteredSegmentsIds);
                updateGroups(filteredSegmentsIds);
            }
        } else {
            updateGroups(initialUniqueSegments.current);
        }
    }, [segmentsGroups, isEditing]);

    const updateUniqueSegmentsIdsByGroup = (groupId: string) => {
        const segmentsIdsFromGroup = getSegmentsIdsFromGroup(groupId, segmentsGroups);
        const mergedSegmentsIds = mergeSegmentIds(initialUniqueSegments.current, segmentsIdsFromGroup.ids);

        dispatch(updateUniqueSegmentsIds(mergedSegmentsIds));
    };

    const updateSegmentsByGroupId = (groupId: string) => {
        if (segmentsGroups) {
            const segmentsIdsFromGroup = getSegmentsIdsFromGroup(groupId, segmentsGroups);
            const segmentsAndWebsitesByGroup = getSegmentsAndWebsitesByGroup(
                accountSegments,
                segmentsGroups,
                groupId,
                segmentsIdsFromGroup,
                segmentsIdsFromGroup.hasWebsites,
            );

            setSelectedGroupSegments(segmentsAndWebsitesByGroup);

            if (segmentsIdsFromGroup?.size > MAX_SEGMENTS_COUNT) {
                const preselectedFirstSevenSegments = getFirstSevenSegments(segmentsIdsFromGroup.ids);
                const preselectedFirstSevenSegmentsArray = getArrayOfSegments(
                    preselectedFirstSevenSegments,
                    segmentsAndWebsitesByGroup,
                );
                const preselectedFirstSevenSegmentsArrayIds = getArrayOfSegmentsId(preselectedFirstSevenSegments);

                setSelectedSegmentsIds(preselectedFirstSevenSegments);
                setSelectedSegments(preselectedFirstSevenSegmentsArray);

                dispatch(selectedSegmentsIdChanged(customDashboard, preselectedFirstSevenSegmentsArrayIds));
                dispatch(dashboardWizardKeyChanged(customDashboard, preselectedFirstSevenSegmentsArrayIds));
            } else {
                const preselectedSegments = getArrayOfSegments(segmentsIdsFromGroup.ids, segmentsAndWebsitesByGroup);
                const preselectedSegmentsIds = getArrayOfSegmentsId(segmentsIdsFromGroup.ids);

                setSelectedSegmentsIds(segmentsIdsFromGroup.ids);

                setSelectedSegments(preselectedSegments);

                dispatch(selectedSegmentsIdChanged(customDashboard, preselectedSegmentsIds));
                dispatch(dashboardWizardKeyChanged(customDashboard, preselectedSegmentsIds));
            }

            setSegments(segmentsAndWebsitesByGroup);
        }
    };

    const updateGroups = (uniqueSegmentsIds: UniqueSegments) => {
        const { SegmentsPerDashboardHardLimit } = swSettings?.current?.resources;

        if (segmentsGroups) {
            if (uniqueSegmentsIds) {
                const result = segmentsGroups.map((segmentGroup) => {
                    const newSegmentGroupIds = new Set([...segmentGroup.segments, ...Object.keys(uniqueSegmentsIds)]);

                    return {
                        ...segmentGroup,
                        isDisabled: newSegmentGroupIds.size > SegmentsPerDashboardHardLimit,
                    };
                });

                setSegmentsGroupsWithFlags(result);
            } else {
                setSegmentsGroupsWithFlags(segmentsGroups);
            }
        }
    };

    const setSelectedSegmentsId = debounce((segment: ICustomSegmentAccount) => {
        if (selectedSegmentsIds[segment.id]) {
            setSelectedSegmentsIds((state) => {
                const newState = { ...state };

                delete newState[segment.id];

                return newState;
            });

            const newSegentsIds = [...Object.keys(selectedSegmentsIds)].filter((id: string) => id !== segment.id);

            const filteredSegements = getArrayOfSegments(newSegentsIds, selectedGroupSegments);

            setSelectedSegments(filteredSegements);

            dispatch(dashboardWizardKeyChanged(customDashboard, newSegentsIds));
            dispatch(selectedSegmentsIdChanged(customDashboard, newSegentsIds));
        } else {
            const newSegentsIds = [...Object.keys(selectedSegmentsIds), segment.id];

            setSelectedSegmentsIds((state) => ({
                ...state,
                [segment.id]: true,
            }));
            setSelectedSegments((state) => [...state, segment]);

            dispatch(dashboardWizardKeyChanged(customDashboard, newSegentsIds));
            dispatch(selectedSegmentsIdChanged(customDashboard, newSegentsIds));
        }
    }, 100);

    const setGroupId = useCallback(
        async (groupId: string) => {
            setSelectedGroupId(groupId);

            updateUniqueSegmentsIdsByGroup(groupId);
            updateSegmentsByGroupId(groupId);

            dispatch(segmentsGroupIdChanged(customDashboard, groupId));
        },
        [customDashboard, currentDashboard, uniqueSegmentsIds],
    );

    return {
        setGroupId,
        setSelectedSegmentsId,
        selectedSegments,
        selectedSegmentsIds,
        segments,
        selectedGroupId,
        segmentsGroupsWithFlags,
    };
};

export default useSegments;
